import type { GetSimulationResponse } from '@/api';
import { Permission, SimulationStatus } from '@/api';
import PermissionWrapper from '@/components/auth/permission-wrapper';
import HeaderInfo from '@/components/page-header/header-info';
import { useUserRole } from '@/hooks/use-user-role';
import { connectColors, themeColors } from '@/theme/colors';
import { Button, Icon } from '@lego/connect-components-react';
import { Flex, Text } from '@lego/klik-ui';
import type { RefObject } from 'react';
import { useSimulationWatchers } from '../../../simulation-queries';
import { useSimulationReadOnly } from '../../context/simulation-read-only-context';

interface SimulationHeaderActionsProps {
  simulationDetails: GetSimulationResponse;
  hasPricelistSettings: boolean;
  priceSettingsTriggerRef: RefObject<HTMLButtonElement | null>;
  onPriceSettingsClick: () => void;
  watchersTriggerRef: RefObject<HTMLButtonElement | null>;
  onWatchersClick: () => void;
  assignToTriggerRef: RefObject<HTMLButtonElement | null>;
  onAssignToClick: () => void;
}

const SimulationHeaderActions = ({
  simulationDetails,
  hasPricelistSettings,
  priceSettingsTriggerRef,
  onPriceSettingsClick,
  watchersTriggerRef,
  onWatchersClick,
  assignToTriggerRef,
  onAssignToClick,
}: SimulationHeaderActionsProps) => {
  const { isReadOnly } = useSimulationReadOnly();
  const { isGlobalPriceManager } = useUserRole();
  const isPublished = simulationDetails.status === SimulationStatus.Published;
  const isDraft = simulationDetails.status === SimulationStatus.Draft;
  const canAssign = isGlobalPriceManager && !isPublished;
  const showWatchers = isGlobalPriceManager && !isDraft;
  const { data: watchers } = useSimulationWatchers(
    simulationDetails.id,
    showWatchers,
  );

  return (
    <Flex flexDirection={'row'} alignItems={'center'}>
      {showWatchers && (
        <Flex flexDirection={'column'} padding={'0 16px'}>
          <Button
            appearance="ghost"
            size="small"
            iconName="chevron-down"
            iconPosition="end"
            aria-label="Watchers"
            style={{ padding: 0 }}
            ref={watchersTriggerRef}
            onClick={onWatchersClick}
            disabled={isPublished}
          >
            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <span
                style={{ color: themeColors.content.subtle }}
                className="body-sm-medium"
              >
                {'Watchers: '}
              </span>
              <span
                style={{ color: themeColors.content.default }}
                className="body-sm-medium"
              >
                {watchers?.length}
              </span>
              <Icon
                name="eye-solid"
                color={'ds-color-content-primary'}
                aria-label="Watchers"
                size="small"
                isChild
              />
            </span>
          </Button>
        </Flex>
      )}
      <Flex
        flexDirection={'column'}
        padding={'0 16px'}
        borderLeft={
          showWatchers
            ? `1px solid ${connectColors.transparent.slate[700]}`
            : undefined
        }
      >
        <Button
          appearance="ghost"
          size="small"
          iconName={canAssign ? 'chevron-down' : undefined}
          iconPosition="end"
          aria-label="Assigned to"
          style={{ padding: 0 }}
          ref={assignToTriggerRef}
          onClick={onAssignToClick}
          disabled={!canAssign}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span
              style={{ color: themeColors.content.subtle }}
              className="body-sm-medium"
            >
              {'Assigned to: '}
            </span>
            <span
              style={{ color: themeColors.content.default }}
              className="body-sm-medium"
            >
              {simulationDetails.assignedTo.name}
            </span>
            <Icon
              name="user-solid"
              color={'ds-color-content-primary'}
              aria-label="Assigned to"
              size="small"
              isChild
            />
          </span>
        </Button>
      </Flex>
      <Flex
        flexDirection={'column'}
        padding={'0 16px'}
        borderLeft={`1px solid ${connectColors.transparent.slate[700]}`}
      >
        <Text color={connectColors.core.offBlack} className="body-sm-medium">
          {simulationDetails.planningValidFrom?.toLocaleDateString('sv-SE')}
        </Text>
      </Flex>
      {simulationDetails.planningPriceUnits !== null &&
        simulationDetails.planningPriceUnits.length > 0 && (
          <HeaderInfo
            data={simulationDetails.planningPriceUnits
              .map((x) => x.name)
              .join(', ')}
          />
        )}

      <Flex flexDirection={'column'} padding={'0 16px'}>
        <Flex alignItems="center" gap="10px">
          <PermissionWrapper
            requiredPermissions={[Permission.EditSimulationSettings]}
          >
            <Button
              aria-label="Price list settings"
              iconName="gear-solid"
              iconPosition="end"
              disabled={!hasPricelistSettings || isReadOnly}
              size="small"
              appearance="ghost"
              style={{
                color: connectColors.core.blue[1000],
                padding: 0,
              }}
              ref={priceSettingsTriggerRef}
              onClick={onPriceSettingsClick}
            >
              Price list settings
            </Button>
          </PermissionWrapper>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default SimulationHeaderActions;
